import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, StepLabel, Stepper } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import OrderAPI from 'api/orbitV1/OrderAPI'
import OrgAPI from 'api/orbitV1/OrgAPI'
import ColorBox from 'common/Box/ColorWhiteBox'
import PageLayout from 'common/Layout/Page/PageLayout'
import StyledStep from 'common/Stepper/StyledStep'
import { ColorBoxContent } from 'common/ui/colorBox'
import format from 'date-fns/format'
import { useFormik } from 'formik'
import { formatAddress } from 'helpers/formatAddress'
import { generateDeliveryDetailsValidationSchema } from 'helpers/formValidationSchema'
import OrbitSession from 'hooks/useCookies'

import DeliveryBillingAddress from './components/DeliveryBillingAddress'
import DeliveryContactDetails from './components/DeliveryContactDetails'
import DeliveryDateTime from './components/DeliveryDateTime'
import DeliverySummary from './components/DeliverySummary'

function BookingDeliveryMain() {
  const [deliveryDate, setDeliveryDate] = useState<Date | null | number>(null)
  const [deliveryTime, setDeliveryTime] = useState<string>('')
  const [activePage, setActivePage] = useState<number>(0)
  const [formattedDeliveryDate, setFormattedDelivery] = useState<string>('')

  const userName = OrbitSession.get('userName')
  const orgId = OrbitSession.get('orgId')
  const userEmail = OrbitSession.get('email')
  const userContactNum = OrbitSession.get('contactNumber')
  const { orderNo } = useParams()

  const setNextPage = () => {
    setActivePage((page) => (page += 1))
  }
  const setPreviousPage = () => {
    setActivePage((page) => (page -= 1))
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      contactName: '',
      contactNum: '',
      formattedDeliveryAddress: '',
      formattedBillingAddress: '',
      deliveryPostalCode: '',
      billingPostalCode: ''
    },
    validationSchema: generateDeliveryDetailsValidationSchema(),
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: orderData, error: orderDataError } = useQuery(
    ['getOrder', orderNo],
    async () => {
      if (orderNo) {
        const { data } = await OrderAPI.getOrderStatus(orderNo)
        return data
      }
    }
  )
  /* useQuery to retrieve Org data */
  const { data: orgData, error: orgDataError } = useQuery(
    ['orgData', orgId],
    async () => {
      const { data } = await OrgAPI.getOrgDetails(orgId)
      return data
    }
  )

  if (orderDataError) {
    toast.error('Error getting order number')
  }

  if (orgDataError) {
    toast.error('Error getting organisation details')
  }

  useEffect(() => {
    if (orgData) {
      const orgDetails = orgData[0]
      const _da = orgDetails.deliveryAddress
      const _ba = orgDetails.billingAddress
      formik.setFieldValue('formattedBillingAddress', formatAddress(_ba))

      formik.setFieldValue('formattedDeliveryAddress', formatAddress(_da))
      formik.setFieldValue('formattedBillingAddress', formatAddress(_ba))

      formik.setFieldValue('deliveryPostalCode', _da.postalCode)
      formik.setFieldValue('billingPostalCode', _ba.postalCode)
    }
  }, [orgData])

  useEffect(() => {
    if (deliveryDate) {
      const formattedDate = format(deliveryDate, 'dd LLLL yyyy')
      setFormattedDelivery(formattedDate)
    }
  }, [deliveryDate])

  useEffect(() => {
    formik.setFieldValue('email', userEmail)
    formik.setFieldValue('contactName', userName)
    formik.setFieldValue('contactNum', userContactNum)
  }, [])

  const pages = [
    <DeliveryDateTime
      key='step1-deliveryDateTime'
      setDeliveryDate={setDeliveryDate}
      setNextPage={setNextPage}
      formattedDeliveryDate={formattedDeliveryDate}
      setDeliveryTime={setDeliveryTime}
      deliveryTime={deliveryTime}
    />,
    <DeliveryBillingAddress
      key='step2-deliveryAddress'
      setPreviousPage={setPreviousPage}
      setNextPage={setNextPage}
      formik={formik}
    />,
    <DeliveryContactDetails
      key='step3-deliveryContactDetails'
      setPreviousPage={setPreviousPage}
      setNextPage={setNextPage}
      formik={formik}
    />,
    <DeliverySummary
      key='step4-deliverySummary'
      setPreviousPage={setPreviousPage}
      formik={formik}
      formattedDeliveryDate={formattedDeliveryDate}
      deliveryTime={deliveryTime}
    />
  ]

  return (
    <>
      <ColorBoxContent>
        <PageLayout header='Book Delivery Slot'>
          <ColorBox>
            <Box sx={{ margin: '24px' }}>
              <Stepper
                orientation='horizontal'
                activeStep={activePage}
                sx={{ margin: '24px 0' }}
              >
                {pages.map((_, index) => (
                  <StyledStep key={`delivery-page${index}`}>
                    <StepLabel></StepLabel>
                  </StyledStep>
                ))}
              </Stepper>
              {pages[activePage]}
            </Box>
          </ColorBox>
        </PageLayout>
      </ColorBoxContent>
    </>
  )
}

export default BookingDeliveryMain
